import { useState } from "react";
import { Copy, MoreHorizontal, Plus, Trash2, X } from "lucide-react";
import CardItem from "@/components/board/CardItem";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Column({
  list,
  lists, // eslint-disable-line no-unused-vars
  cards,
  onAddCard,
  onCardClick,
  onDeleteList,
  onDuplicateList,
  onRenameList,
  onDragStartCard,
  onDragEndCard,
  onDropCard,
  onMoveCard, // eslint-disable-line no-unused-vars
  draggingCardId,
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(list.title);
  const [isOver, setIsOver] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onAddCard(list.id, t);
    setText("");
    setAdding(false);
  };

  const submitRename = (e) => {
    e.preventDefault();
    const t = title.trim();
    if (t && t !== list.title) onRenameList(list.id, t);
    setRenaming(false);
  };

  return (
    <div
      className="w-full sm:w-[320px] min-w-full sm:min-w-[320px] flex flex-col border-r border-zinc-200 dark:border-zinc-800 h-full bg-zinc-50/30 dark:bg-zinc-900/20 snap-start sm:snap-align-none shrink-0"
      data-testid={`kanban-column-${list.id}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!isOver) setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        onDropCard(list.id, null);
      }}
    >
      <div className="px-4 py-3 flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shrink-0">
        {renaming ? (
          <form onSubmit={submitRename} className="flex-1">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={submitRename}
              className="w-full text-sm font-bold uppercase tracking-[0.1em] border-b border-black bg-transparent focus:outline-none"
              data-testid={`column-rename-input-${list.id}`}
            />
          </form>
        ) : (
          <button
            onClick={() => setRenaming(true)}
            className="text-sm font-bold uppercase tracking-[0.1em] text-zinc-950 dark:text-zinc-100 hover:text-zinc-500 dark:hover:text-zinc-400 transition-colors truncate"
            data-testid={`column-title-${list.id}`}
          >
            {list.title}
            <span className="ml-2 text-zinc-400 font-mono-tight">{cards.length}</span>
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2 -m-1 text-zinc-500 hover:text-black dark:hover:text-white active:text-black transition-colors shrink-0"
              data-testid={`column-menu-${list.id}`}
              aria-label="Column menu"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none border-zinc-200 dark:border-zinc-800">
            <DropdownMenuItem onClick={() => setRenaming(true)}>Rename</DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDuplicateList(list.id)}
              data-testid={`column-duplicate-${list.id}`}
            >
              <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate list
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                if (window.confirm("Delete this list and all its cards?")) onDeleteList(list.id);
              }}
              className="text-red-600 focus:text-red-600"
              data-testid={`column-delete-${list.id}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete list
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div
        className={`flex-1 overflow-y-auto p-3 flex flex-col gap-2 ${
          isOver ? "bg-zinc-100 dark:bg-zinc-800/50" : ""
        }`}
      >
        {cards.map((c) => (
          <CardItem
            key={c.id}
            card={c}
            onClick={() => onCardClick(c)}
            onDragStart={onDragStartCard}
            onDragEnd={onDragEndCard}
            isDragging={draggingCardId === c.id}
          />
        ))}

        {adding ? (
          <form onSubmit={submit} className="border border-black dark:border-white bg-white dark:bg-zinc-950 p-2" data-testid={`add-card-form-${list.id}`}>
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              placeholder="Card title..."
              className="w-full text-base sm:text-sm resize-none focus:outline-none bg-transparent text-zinc-950 dark:text-zinc-100"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit(e);
                }
              }}
              data-testid={`add-card-input-${list.id}`}
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                type="submit"
                className="bg-black dark:bg-white text-white dark:text-black px-3 py-2 text-xs font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200"
                data-testid={`add-card-submit-${list.id}`}
              >
                Add card
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdding(false);
                  setText("");
                }}
                className="p-2 text-zinc-500 hover:text-black dark:hover:text-white"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="text-xs text-zinc-500 hover:text-black dark:hover:text-white active:text-black transition-colors flex items-center gap-1.5 py-3 px-1 text-left"
            data-testid={`add-card-button-${list.id}`}
          >
            <Plus className="h-3.5 w-3.5" /> Add a card
          </button>
        )}
      </div>
    </div>
  );
}
